import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Video, Image as ImageIcon, Play, RefreshCw, MonitorPlay, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DeviceRule, DeviceType, DeviceAction } from '@/store/videoFeatures/types/responsive-player';
import { ImageDropzone } from '@/components/ui/image-dropzone';
import { cn } from '@/lib/utils';
import { handleImageUploadWithCompression } from '@/utils/compressedImageUpload';
import { VideoFolderPicker } from '@/components/shared/VideoFolderPicker';
import { LibraryVideo } from './ResponsivePlayerConfig';

interface DeviceTabProps {
  device: DeviceType;
  rule: DeviceRule;
  onUpdate: (updates: Partial<DeviceRule>) => void;
  libraryVideos: LibraryVideo[];
  isLoading?: boolean;
}

export const DeviceTab: React.FC<DeviceTabProps> = ({
  device,
  rule,
  onUpdate,
  libraryVideos,
  isLoading = false,
}) => {
  useLanguage();

  // Garante que o vídeo já selecionado apareça no trigger mesmo que não
  // esteja mais na biblioteca atual (ex.: regra salva anteriormente).
  const pickerVideos = useMemo(() => {
    const base = libraryVideos.map((v) => ({ id: v.id, title: v.title, thumbnailUrl: v.thumbnailUrl }));
    const selId = rule.alternativeVideo?.videoId;
    if (selId && !base.some((v) => v.id === selId)) {
      base.unshift({
        id: selId,
        title: rule.alternativeVideo?.videoTitle || selId,
        thumbnailUrl: rule.alternativeVideo?.thumbnailUrl || '',
      });
    }
    return base;
  }, [libraryVideos, rule.alternativeVideo]);

  const handleVideoLibrarySelect = (videoId: string) => {
    const video = libraryVideos.find(v => v.id === videoId);
    if (!video) return;
    onUpdate({
      alternativeVideo: {
        videoId: video.id,
        videoTitle: video.title,
        thumbnailUrl: video.thumbnailUrl,
      }
    });
  };

  const [isUploadExpanded, setIsUploadExpanded] = useState(false);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<'16:9' | '9:16'>('16:9');
  const [tempImageUrl, setTempImageUrl] = useState<string>('');
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageCardRef = useRef<HTMLDivElement>(null);

  // Scroll automático ao selecionar "Mostrar Imagem"
  useEffect(() => {
    if (rule.action === 'show-image' && imageCardRef.current) {
      setTimeout(() => {
        imageCardRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 150);
    }
  }, [rule.action]);

  // Sincronizar aspect ratio automaticamente quando mudar
  useEffect(() => {
    if (tempImageUrl) {
      onUpdate({
        imageReplacement: {
          imageUrl: tempImageUrl,
          aspectRatio: selectedAspectRatio
        }
      });
    }
  }, [selectedAspectRatio]);

  const handleBehaviorChange = (behavior: DeviceAction) => {
    const updates: Partial<DeviceRule> = { action: behavior };
    
    // Limpar dados não relacionados
    if (behavior !== 'show-alternative') {
      updates.alternativeVideo = undefined;
    }
    if (behavior !== 'show-image') {
      updates.imageReplacement = undefined;
    }
    
    onUpdate(updates);
  };

  const handleVideoClear = () => {
    onUpdate({ alternativeVideo: undefined });
  };

  const handleFileSelect = (file: File) => {
    // Usar compressão agressiva para evitar QuotaExceededError
    handleImageUploadWithCompression(
      file,
      (compressedBase64) => {
        setTempImageUrl(compressedBase64);
        // Salvar automaticamente ao selecionar imagem (já comprimida)
        onUpdate({
          imageReplacement: {
            imageUrl: compressedBase64,
            aspectRatio: selectedAspectRatio
          }
        });
      },
      { maxWidth: 800, quality: 0.6, showFeedback: true }
    );
  };

  const handleExpandUpload = () => {
    setTempImageUrl(rule.imageReplacement?.imageUrl || '');
    setSelectedAspectRatio(rule.imageReplacement?.aspectRatio || '16:9');
    setIsUploadExpanded(true);
  };

  const handleCancelUpload = () => {
    setTempImageUrl('');
    setIsUploadExpanded(false);
  };

  const handleSaveImage = () => {
    if (tempImageUrl) {
      onUpdate({
        imageReplacement: {
          imageUrl: tempImageUrl,
          aspectRatio: selectedAspectRatio
        }
      });
    }
    setTempImageUrl('');
    setIsUploadExpanded(false);
  };

  const handleRemoveImage = () => {
    setTempImageUrl('');
    onUpdate({ imageReplacement: undefined });
    setShowRemoveConfirmation(false);
  };

  const deviceLabel = device === 'desktop' ? 'Desktop' : device === 'tablet' ? 'Tablet' : 'Mobile';

  const getActionLabel = (action: DeviceAction) => {
    switch (action) {
      case 'show-original': return translateInterfaceText("Exibir Vídeo Original");
      case 'show-alternative': return translateInterfaceText("Substituir por Outro Vídeo");
      case 'show-image': return translateInterfaceText("Mostrar Imagem");
      default: return translateInterfaceText("Selecione o comportamento");
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Select Principal - Comportamento do Player */}
      <Card className="rounded-2xl border border-[#EEF1F5] shadow-[0_4px_12px_rgba(0,0,0,0.04)] max-w-[480px] bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-[15px] font-semibold flex items-center gap-2.5 text-[#1A1A1A]">
            <div className="w-8 h-8 rounded-lg bg-[#1D4FFF]/10 flex items-center justify-center">
              <MonitorPlay className="h-4 w-4 text-[#1D4FFF]" />
            </div>
            {translateInterfaceText("Comportamento do Player")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <Select 
            key={`${device}-${rule.action}`}
            value={rule.action} 
            onValueChange={handleBehaviorChange}
          >
            <SelectTrigger className="w-full h-12 rounded-xl border-[#E6EAF2] px-4 text-[14px] font-medium hover:border-[#1D4FFF]/40 transition-colors bg-white">
              <span className={rule.action === 'show-original' ? "text-[#6B7280]" : "text-[#1F2937]"}>{getActionLabel(rule.action)}</span>
            </SelectTrigger>
            <SelectContent className="rounded-xl border-[#E6EAF2] shadow-[0_8px_24px_rgba(0,0,0,0.12)] bg-white z-50">
              {/* Header interno do dropdown */}
              <div className="px-3 py-2.5 border-b border-[#E6EAF2]">
                <p className="text-[12px] font-medium text-[#6B7280]">
                  {translateInterfaceText("Escolha como o player será exibido neste dispositivo.")}</p>
              </div>
              <SelectItem 
                value="show-original" 
                className="rounded-lg py-3 px-3 cursor-pointer transition-colors hover:bg-[#3B82F6]/[0.08] focus:bg-[#3B82F6]/[0.08] data-[state=checked]:bg-[#1D4FFF]/10 data-[state=checked]:border-l-[3px] data-[state=checked]:border-l-[#1D4FFF] data-[state=checked]:pl-[9px]"
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                    rule.action === 'show-original' ? "bg-[#1D4FFF]/15" : "bg-[#F7F9FC]"
                  )}>
                    <Play className={cn("h-4 w-4 transition-colors", rule.action === 'show-original' ? "text-[#1D4FFF]" : "text-[#1D4FFF]")} />
                  </div>
                  <div>
                    <div className="text-[13px] font-medium text-[#1F2937]">{translateInterfaceText("Exibir Vídeo Original")}</div>
                    <div className="text-[10px] text-[#6B7280]">{translateInterfaceText("Mantém o vídeo padrão neste dispositivo")}</div>
                  </div>
                </div>
              </SelectItem>
              <SelectItem 
                value="show-alternative" 
                className="rounded-lg py-3 px-3 cursor-pointer transition-colors hover:bg-[#3B82F6]/[0.08] focus:bg-[#3B82F6]/[0.08] data-[state=checked]:bg-[#1D4FFF]/10 data-[state=checked]:border-l-[3px] data-[state=checked]:border-l-[#1D4FFF] data-[state=checked]:pl-[9px]"
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                    rule.action === 'show-alternative' ? "bg-[#1D4FFF]/15" : "bg-[#F7F9FC]"
                  )}>
                    <Video className={cn("h-4 w-4 transition-colors", rule.action === 'show-alternative' ? "text-[#1D4FFF]" : "text-[#1D4FFF]")} />
                  </div>
                  <div>
                    <div className="text-[13px] font-medium text-[#1F2937]">{translateInterfaceText("Substituir por Outro Vídeo")}</div>
                    <div className="text-[10px] text-[#6B7280]">{translateInterfaceText("Mostra um vídeo diferente apenas neste dispositivo")}</div>
                  </div>
                </div>
              </SelectItem>
              <SelectItem 
                value="show-image" 
                className="rounded-lg py-3 px-3 cursor-pointer transition-colors hover:bg-[#3B82F6]/[0.08] focus:bg-[#3B82F6]/[0.08] data-[state=checked]:bg-[#1D4FFF]/10 data-[state=checked]:border-l-[3px] data-[state=checked]:border-l-[#1D4FFF] data-[state=checked]:pl-[9px]"
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                    rule.action === 'show-image' ? "bg-[#1D4FFF]/15" : "bg-[#F7F9FC]"
                  )}>
                    <ImageIcon className={cn("h-4 w-4 transition-colors", rule.action === 'show-image' ? "text-[#1D4FFF]" : "text-[#1D4FFF]")} />
                  </div>
                  <div>
                    <div className="text-[13px] font-medium text-[#1F2937]">{translateInterfaceText("Mostrar Imagem")}</div>
                    <div className="text-[10px] text-[#6B7280]">{translateInterfaceText("Exibe uma imagem estática no lugar do player")}</div>
                  </div>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Conteúdo Condicional: Vídeo Alternativo */}
      {rule.action === 'show-alternative' && (
        <Card className="rounded-2xl border border-[#EEF1F5] shadow-[0_4px_12px_rgba(0,0,0,0.04)] max-w-[480px] bg-white">
          <CardHeader className="pb-3">
            <CardTitle className="text-[15px] font-semibold flex items-center gap-2.5 text-[#1A1A1A]">
              <div className="w-8 h-8 rounded-lg bg-[#1D4FFF]/10 flex items-center justify-center">
                <Video className="h-4 w-4 text-[#1D4FFF]" />
              </div>
              {translateInterfaceText("Selecionar Vídeo")}</CardTitle>
            <p className="text-[11px] text-[#6B7280] mt-1.5 ml-[42px]">
              {translateInterfaceText("Escolha um vídeo da biblioteca para substituir o original.")}</p>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <VideoFolderPicker
              value={rule.alternativeVideo?.videoId}
              videos={pickerVideos}
              onChange={handleVideoLibrarySelect}
              placeholder={isLoading ? translateInterfaceText("Carregando vídeos...") : translateInterfaceText("Escolha um vídeo da biblioteca")}
              disabled={isLoading}
              triggerClassName="flex w-full h-12 items-center justify-between gap-2 rounded-xl border border-[#E6EAF2] px-4 text-[14px] text-[#1F2937] hover:border-[#1D4FFF]/40 transition-colors bg-white disabled:opacity-50"
            />

            {rule.alternativeVideo?.videoId && (
              <button
                onClick={handleVideoClear}
                className="text-[11px] text-[#EF4444] hover:underline"
              >
                {translateInterfaceText("Remover seleção")}</button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Conteúdo Condicional: Imagem Substituta */}
      {rule.action === 'show-image' && (
        <Card 
          ref={imageCardRef}
          className="rounded-2xl border border-[#EEF1F5] shadow-[0_4px_12px_rgba(0,0,0,0.04)] max-w-[480px] bg-white"
        >
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#1D4FFF]/10 flex items-center justify-center">
                <ImageIcon className="h-4 w-4 text-[#1D4FFF]" />
              </div>
              <div>
                <CardTitle className="text-[15px] font-semibold text-[#1A1A1A]">
                  {translateInterfaceText("Imagem Substituta")}</CardTitle>
                <p className="text-[11px] text-[#6B7280] mt-0.5">
                  {translateInterfaceText("Envie uma imagem para substituir o vídeo.")}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            {/* Preview da imagem existente (quando não está expandido) - com overlay de ações */}
            {rule.imageReplacement?.imageUrl && !isUploadExpanded ? (
              <div className="space-y-3">
                <div 
                  className="relative w-full aspect-video rounded-xl overflow-hidden border border-[#E6EAF2] group cursor-pointer"
                >
                  <img 
                    src={rule.imageReplacement.imageUrl} 
                    alt={translateInterfaceText("Preview")}
                    className="w-full h-full object-cover transition-all duration-200 group-hover:brightness-75"
                  />
                  
                  {/* Overlay com ações */}
                  <div className="absolute inset-0 bg-black/70 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center gap-6 rounded-xl">
                    {/* Botão Trocar */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExpandUpload();
                      }}
                      className="flex flex-col items-center gap-2 group/btn"
                    >
                      <div className="w-11 h-11 rounded-full bg-[#1D4FFF] flex items-center justify-center transition-transform group-hover/btn:scale-110">
                        <RefreshCw className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-white text-[12px] font-medium">{translateInterfaceText("Trocar")}</span>
                    </button>

                    {/* Botão Remover */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowRemoveConfirmation(true);
                      }}
                      className="flex flex-col items-center gap-2 group/btn"
                    >
                      <div className="w-11 h-11 rounded-full bg-red-500 flex items-center justify-center transition-transform group-hover/btn:scale-110">
                        <Trash2 className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-white text-[12px] font-medium">{translateInterfaceText("Remover")}</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : !isUploadExpanded ? (
              /* Área vazia - clique para expandir - mais compacta */
              <button
                onClick={handleExpandUpload}
                className="w-full rounded-xl border-2 border-dashed border-[#E6EAF2] bg-[#F7F9FC] p-6 text-center hover:border-[#1D4FFF]/40 hover:bg-[#F0F7FF] transition-colors"
              >
                <ImageIcon className="mx-auto h-10 w-10 text-[#64748B]/50 mb-2" />
                <p className="text-[13px] font-medium text-[#1A1A1A] mb-0.5">{translateInterfaceText("Enviar Imagem")}</p>
                <p className="text-[11px] text-[#6B7280]">
                  {translateInterfaceText("Clique para escolher. Máximo: 5MB")}</p>
              </button>
            ) : null}

            {/* Seção de Upload Inline (Collapsible) */}
            {isUploadExpanded && (
              <div className="space-y-4 animate-in slide-in-from-top-2 duration-200">
                {/* Seleção de Formato - Card Selector Premium */}
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium text-[#6B7280]">{translateInterfaceText("Formato da Imagem")}</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedAspectRatio('16:9')}
                      className={cn(
                        "flex-1 px-4 py-3 rounded-xl border-2 transition-all flex items-center gap-3",
                        selectedAspectRatio === '16:9' 
                          ? "border-[#1D4FFF] bg-[#1D4FFF] text-white" 
                          : "border-[#E6EAF2] bg-white hover:border-[#1D4FFF]/40 text-[#1A1A1A]"
                      )}
                    >
                      <div className={cn(
                        "w-8 h-5 rounded border flex-shrink-0",
                        selectedAspectRatio === '16:9' 
                          ? "border-white/50 bg-white/20" 
                          : "border-[#E6EAF2]"
                      )} />
                      <div className="text-left">
                        <div className="text-[13px] font-medium">16:9</div>
                        <div className={cn(
                          "text-[10px]",
                          selectedAspectRatio === '16:9' ? "text-white/70" : "text-[#6B7280]"
                        )}>1920×1080</div>
                      </div>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setSelectedAspectRatio('9:16')}
                      className={cn(
                        "flex-1 px-4 py-3 rounded-xl border-2 transition-all flex items-center gap-3",
                        selectedAspectRatio === '9:16' 
                          ? "border-[#1D4FFF] bg-[#1D4FFF] text-white" 
                          : "border-[#E6EAF2] bg-white hover:border-[#1D4FFF]/40 text-[#1A1A1A]"
                      )}
                    >
                      <div className={cn(
                        "w-5 h-8 rounded border flex-shrink-0",
                        selectedAspectRatio === '9:16' 
                          ? "border-white/50 bg-white/20" 
                          : "border-[#E6EAF2]"
                      )} />
                      <div className="text-left">
                        <div className="text-[13px] font-medium">9:16</div>
                        <div className={cn(
                          "text-[10px]",
                          selectedAspectRatio === '9:16' ? "text-white/70" : "text-[#6B7280]"
                        )}>1080×1920</div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Upload / Preview */}
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium text-[#6B7280]">{translateInterfaceText("Upload")}</Label>
                  {!tempImageUrl ? (
                    <ImageDropzone
                      currentFile={undefined}
                      onFileSelect={handleFileSelect}
                      onRemove={() => {}}
                      label={translateInterfaceText("Arraste ou clique para enviar")}
                      sublabel={translateInterfaceText("PNG/JPG/GIF • Máx. 5MB")}
                      maxSizeMB={5}
                      acceptedFormats={['image/png', 'image/jpeg', 'image/gif']}
                    />
                  ) : (
                    <div className="space-y-3">
                      {/* Preview da imagem com overlay de ações */}
                      <div className={cn(
                        "relative rounded-xl overflow-hidden border border-[#E6EAF2] group cursor-pointer",
                        selectedAspectRatio === '9:16' 
                          ? "aspect-[9/16] max-w-[180px] mx-auto" 
                          : "aspect-video max-w-sm mx-auto"
                      )}>
                        <img 
                          src={tempImageUrl} 
                          alt={translateInterfaceText("Preview")}
                          className="w-full h-full object-cover transition-all duration-200 group-hover:brightness-75"
                        />
                        
                        {/* Overlay com ações */}
                        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center gap-6 rounded-xl">
                          {/* Botão Trocar */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              fileInputRef.current?.click();
                            }}
                            className="flex flex-col items-center gap-2 group/btn"
                          >
                            <div className="w-11 h-11 rounded-full bg-[#1D4FFF] flex items-center justify-center transition-transform group-hover/btn:scale-110">
                              <RefreshCw className="h-5 w-5 text-white" />
                            </div>
                            <span className="text-white text-[12px] font-medium">{translateInterfaceText("Trocar")}</span>
                          </button>

                          {/* Botão Remover */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowRemoveConfirmation(true);
                            }}
                            className="flex flex-col items-center gap-2 group/btn"
                          >
                            <div className="w-11 h-11 rounded-full bg-red-500 flex items-center justify-center transition-transform group-hover/btn:scale-110">
                              <Trash2 className="h-5 w-5 text-white" />
                            </div>
                            <span className="text-white text-[12px] font-medium">{translateInterfaceText("Remover")}</span>
                          </button>
                        </div>
                      </div>
                      
                      {/* Input de arquivo oculto */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileSelect(file);
                          }
                        }}
                        className="hidden"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modal de Confirmação de Remoção */}
      <AlertDialog open={showRemoveConfirmation} onOpenChange={setShowRemoveConfirmation}>
        <AlertDialogContent className="w-[380px] max-w-[90vw] rounded-2xl p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">{translateInterfaceText("Remover imagem")}</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {translateInterfaceText("Tem certeza que deseja remover esta imagem?")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-9 px-4 text-muted-foreground border-border">
              {translateInterfaceText("Cancelar")}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRemoveImage}
              className="h-9 px-4 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {translateInterfaceText("Remover")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};